import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, Workflow, Users, CheckCircle2, Percent, X, Pencil, Loader2, IndianRupee, ArrowLeft, ChevronDown, CalendarDays, Sparkles, ListChecks } from "lucide-react";
import { GROWTHOS_BASE, authHeaders } from "../config/api";
import AIMediaWorkflow from "./workflow";
import CampaignCalendar, { useContentCalendarStore } from "./CampaignCalendar";
import PlannedPosts from "./PlannedPosts";

const CHANNEL_OPTIONS = ["Email", "WhatsApp", "SMS", "Email, WhatsApp", "Email, SMS"];
const STATUS_OPTIONS = ["Draft", "Running", "Paused"];

// Tabs — same pattern as Business Workspace (click a tab → only that section shows).
const TABS = [
    { label: "Create Campaign", icon: Workflow },
    { label: "Generate Content", icon: Sparkles },
    { label: "Schedule & Post", icon: CalendarDays },
    { label: "Planned Posts", icon: ListChecks },
];

const AUTOMATIONS_ENDPOINT = `${GROWTHOS_BASE}/automations`;

// TEMP: sample Facebook image in "Saved content" so the scheduler can be tested
// while image generation is down. Set to false (or delete) when no longer needed.
const SHOW_DEMO_CONTENT = true;
const DEMO_CONTENT = {
    id: "demo_facebook_sample",
    outputs: { facebook: { url: "https://picsum.photos/id/237/1080/1080.jpg", type: "image" } },
};

// Themed scrollbar — same as Lead Management: thin, transparent track with a
// subtle orange thumb (WebKit pseudo-elements + Firefox scrollbar-* props).
const THEMED_SCROLLBAR =
    "[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 " +
    "[&::-webkit-scrollbar-track]:bg-transparent " +
    "[&::-webkit-scrollbar-thumb]:bg-orange-500/20 [&::-webkit-scrollbar-thumb]:rounded-full " +
    "[&::-webkit-scrollbar-thumb:hover]:bg-orange-500/40 " +
    "[scrollbar-width:thin] [scrollbar-color:rgba(249,115,22,0.25)_transparent]";

// /automations is now scoped per logged-in company on the backend — every
// request needs this Authorization header or it 401s (and, before that
// backend fix, silently mixed every company's automations together).
const statusStyles = {
    Running: "bg-green-500/10 text-green-400 border-green-600/30",
    Paused: "bg-gray-500/10 text-gray-400 border-gray-600/30",
    Draft: "bg-purple-500/10 text-purple-400 border-purple-600/30",
};

const emptyForm = { name: "", channel: CHANNEL_OPTIONS[0], status: "Draft", budget: "" };

/* ==========================================================
   CREATE AUTOMATION MODAL
   Simple controlled form — validates name is
   filled, then hands the new automation back to the parent.
========================================================== */
const CreateAutomationModal = ({ onClose, onCreate, creating, createError }) => {
    const [form, setForm] = useState(emptyForm);
    const [error, setError] = useState("");

    const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            setError("Workflow name is required.");
            return;
        }
        setError("");
        onCreate({
            name: form.name.trim(),
            channel: form.channel,
            status: form.status,
            budget: Math.max(0, Number(form.budget) || 0),
        });
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
            onClick={onClose}
        >
            <div
                className="bg-[#0a0a0a] border border-orange-600/30 rounded-2xl w-full max-w-lg p-6 sm:p-8 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-5 right-5 text-gray-500 hover:text-white transition"
                    aria-label="Close"
                >
                    <X size={20} />
                </button>

                <h2 className="text-xl font-bold text-white">Create Automation</h2>
                <p className="text-gray-400 text-sm mt-1">
                    Set up a new automated workflow.
                </p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                    <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                            Workflow Name
                        </label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={update("name")}
                            placeholder="e.g. Birthday Offer Flow"
                            className="w-full bg-white/5 border border-white/10 focus:border-orange-600/50 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-gray-600 outline-none transition"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                                Channel
                            </label>
                            <select
                                value={form.channel}
                                onChange={update("channel")}
                                className="w-full bg-white/5 border border-white/10 focus:border-orange-600/50 rounded-lg px-3 py-2.5 text-sm text-white outline-none transition"
                            >
                                {CHANNEL_OPTIONS.map((c) => (
                                    <option key={c} value={c} className="bg-[#0a0a0a]">
                                        {c}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                                Status
                            </label>
                            <select
                                value={form.status}
                                onChange={update("status")}
                                className="w-full bg-white/5 border border-white/10 focus:border-orange-600/50 rounded-lg px-3 py-2.5 text-sm text-white outline-none transition"
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s} className="bg-[#0a0a0a]">
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                            Budget (₹)
                        </label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.budget}
                            onChange={update("budget")}
                            placeholder="e.g. 25000"
                            className="w-full bg-white/5 border border-white/10 focus:border-orange-600/50 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-gray-600 outline-none transition"
                        />
                        <p className="text-gray-500 text-[11px] mt-1">
                            Planned/actual spend for this workflow — feeds the Marketing Spend card in Analytics & BI.
                        </p>
                    </div>

                    {(error || createError) && <p className="text-red-400 text-xs">{error || createError}</p>}

                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={creating}
                            className="px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-300 hover:text-white hover:bg-white/5 transition disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={creating}
                            className="bg-orange-500 text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-orange-600 transition disabled:opacity-60"
                        >
                            {creating ? "Saving..." : "Create Automation"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/* ==========================================================
   EDIT AUTOMATION STATS MODAL
   Manually update status/enrolled/completed/conversion — since
   there's no execution engine wired up yet to fill these in
   automatically, the person enters real numbers themselves
   (e.g. copied from the WhatsApp/Email provider's own dashboard).
========================================================== */
const EditAutomationModal = ({ automation, onClose, onSave, saving, saveError }) => {
    const [form, setForm] = useState({
        status: automation.status,
        enrolled: String(automation.enrolled ?? 0),
        completed: String(automation.completed ?? 0),
        conversion_rate: String(automation.conversion_rate ?? 0),
        budget: String(automation.budget ?? 0),
    });

    const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({
            status: form.status,
            enrolled: Math.max(0, Number(form.enrolled) || 0),
            completed: Math.max(0, Number(form.completed) || 0),
            conversion_rate: Math.max(0, Number(form.conversion_rate) || 0),
            budget: Math.max(0, Number(form.budget) || 0),
        });
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
            onClick={onClose}
        >
            <div
                className="bg-[#0a0a0a] border border-orange-600/30 rounded-2xl w-full max-w-md p-6 sm:p-8 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-5 right-5 text-gray-500 hover:text-white transition"
                    aria-label="Close"
                >
                    <X size={20} />
                </button>

                <h2 className="text-xl font-bold text-white">Update Stats</h2>
                <p className="text-gray-400 text-sm mt-1 truncate">{automation.workflow_name}</p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                    <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1.5">Status</label>
                        <select
                            value={form.status}
                            onChange={update("status")}
                            className="w-full bg-white/5 border border-white/10 focus:border-orange-600/50 rounded-lg px-3 py-2.5 text-sm text-white outline-none transition"
                        >
                            {STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s} className="bg-[#0a0a0a]">
                                    {s}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-400 mb-1.5">Enrolled</label>
                            <input
                                type="number"
                                min="0"
                                value={form.enrolled}
                                onChange={update("enrolled")}
                                className="w-full bg-white/5 border border-white/10 focus:border-orange-600/50 rounded-lg px-3 py-2.5 text-sm text-white outline-none transition"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-400 mb-1.5">Completed</label>
                            <input
                                type="number"
                                min="0"
                                value={form.completed}
                                onChange={update("completed")}
                                className="w-full bg-white/5 border border-white/10 focus:border-orange-600/50 rounded-lg px-3 py-2.5 text-sm text-white outline-none transition"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-400 mb-1.5">Conversion Rate (%)</label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={form.conversion_rate}
                                onChange={update("conversion_rate")}
                                className="w-full bg-white/5 border border-white/10 focus:border-orange-600/50 rounded-lg px-3 py-2.5 text-sm text-white outline-none transition"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-400 mb-1.5">Budget (₹)</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.budget}
                                onChange={update("budget")}
                                className="w-full bg-white/5 border border-white/10 focus:border-orange-600/50 rounded-lg px-3 py-2.5 text-sm text-white outline-none transition"
                            />
                        </div>
                    </div>

                    {saveError && <p className="text-red-400 text-xs">{saveError}</p>}

                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            className="px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-300 hover:text-white hover:bg-white/5 transition disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex items-center gap-1.5 bg-orange-500 text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-orange-600 transition disabled:opacity-60"
                        >
                            {saving && <Loader2 size={14} className="animate-spin" />}
                            {saving ? "Saving..." : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/* ==========================================================
   CAMPAIGN AUTOMATION (id="sec-campaign-automation")
========================================================== */
const CampaignAutomation = () => {
    const [automations, setAutomations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [isModalOpen, setModalOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState("");
    // Table body stays hidden until the person clicks the column-header row.
    const [tableOpen, setTableOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(TABS[0].label);

    // Saved content + calendar entries (shared by the calendar and the AI Media Generator)
    const calendarStore = useContentCalendarStore();

    // Step 1 → Step 2 bridge: remember the latest generated content so we can
    // offer "Schedule this content" and open the calendar's schedule modal for it.
    const [readyContentId, setReadyContentId] = useState(null);
    const [scheduleRequest, setScheduleRequest] = useState(null);
    const readyContent = calendarStore.library.find((x) => x.id === readyContentId);
    const handleContentReady = (payload) => {
        calendarStore.addToLibrary(payload);
        if (payload?.id) setReadyContentId(payload.id);
    };
    useEffect(() => {
        if (SHOW_DEMO_CONTENT) calendarStore.addToLibrary(DEMO_CONTENT);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleScheduleReady = () => {
        setScheduleRequest({ contentId: readyContentId, nonce: Date.now() });
        setActiveTab("Schedule & Post");
    };

    const [editingAutomation, setEditingAutomation] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);
    const [saveEditError, setSaveEditError] = useState("");

    const fetchAutomations = async () => {
        setLoading(true);
        setLoadError("");
        try {
            const res = await fetch(AUTOMATIONS_ENDPOINT, { method: "GET", headers: { ...authHeaders() } });
            const resp = await res.json();
            if (res.ok && resp.success && Array.isArray(resp.data)) {
                setAutomations(resp.data);
            } else {
                setAutomations([]);
                setLoadError(resp.message || "Couldn't load automations.");
            }
        } catch (err) {
            setAutomations([]);
            setLoadError("Couldn't load automations. Check your connection and try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAutomations();
    }, []);

    // Arrived here via Dashboard's "Create Campaign" Quick Action? Open the
    // create-automation modal automatically, then clear the nav state so a
    // refresh/back doesn't reopen it.
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        if (location.state?.openCreateAutomation) {
            setModalOpen(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    // Saves to isfathena.campaign_automations via main.py — no more
    // local-only state. Field names/types are mapped to what the backend
    // expects (workflow_name, conversion_rate, enrolled/completed as
    // numbers) before posting.
    const handleCreate = async (newAutomation) => {
        setCreating(true);
        setCreateError("");
        try {
            const res = await fetch(AUTOMATIONS_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({
                    workflow_name: newAutomation.name,
                    channel: newAutomation.channel,
                    status: newAutomation.status,
                    enrolled: 0,
                    completed: 0,
                    conversion_rate: 0,
                    budget: newAutomation.budget,
                }),
            });
            const resp = await res.json().catch(() => null);
            if (!res.ok || !resp?.success) {
                throw new Error(resp?.message || "Could not save automation");
            }
            setModalOpen(false);
            setTableOpen(true); // show the new row right away
            fetchAutomations(); // refresh from the DB so the table shows the real saved row
        } catch (err) {
            setCreateError("Could not save this automation. Please try again.");
        } finally {
            setCreating(false);
        }
    };

    const handleUpdateStats = async (statsPayload) => {
        if (!editingAutomation) return;
        setSavingEdit(true);
        setSaveEditError("");
        try {
            const res = await fetch(`${AUTOMATIONS_ENDPOINT}/${editingAutomation.id}/stats`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify(statsPayload),
            });
            const resp = await res.json().catch(() => null);
            if (!res.ok || !resp?.success) {
                throw new Error(resp?.error || "Could not update automation");
            }
            setEditingAutomation(null);
            fetchAutomations(); // refresh so the KPI cards + table reflect the new numbers
        } catch (err) {
            setSaveEditError("Could not save changes. Please try again.");
        } finally {
            setSavingEdit(false);
        }
    };

    // A load error must never stay hidden behind the collapsed table.
    const showBody = tableOpen || !!loadError;

    const runningCount = automations.filter((a) => a.status === "Running").length;

    // Real sums/averages from the fetched automations — no fabricated
    // month-over-month deltas since no history is tracked yet.
    const totalEnrolled = automations.reduce((sum, a) => sum + (Number(a.enrolled) || 0), 0);
    const totalCompleted = automations.reduce((sum, a) => sum + (Number(a.completed) || 0), 0);
    const completionRate = totalEnrolled > 0 ? ((totalCompleted / totalEnrolled) * 100).toFixed(1) : "0.0";
    // Weighted by enrollment so a workflow with 2 contacts doesn't skew the
    // average as much as one with 2,000.
    const avgConversion =
        totalEnrolled > 0
            ? (automations.reduce((sum, a) => sum + (Number(a.conversion_rate) || 0) * (Number(a.enrolled) || 0), 0) / totalEnrolled).toFixed(1)
            : "0.0";
    const totalBudget = automations.reduce((sum, a) => sum + (Number(a.budget) || 0), 0);
    const formatBudget = (num) => {
        const n = Number(num) || 0;
        return n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : `₹${n.toLocaleString("en-IN")}`;
    };

    const automationKpis = [
        { icon: Workflow, value: String(automations.length), label: "Total Workflows", sub: `${runningCount} running` },
        { icon: IndianRupee, value: formatBudget(totalBudget), label: "Total Budget", sub: `Across ${automations.length} workflow${automations.length !== 1 ? "s" : ""}` },
        { icon: Users, value: totalEnrolled.toLocaleString("en-IN"), label: "Contacts Enrolled", sub: `Across ${automations.length} workflow${automations.length !== 1 ? "s" : ""}` },
        { icon: CheckCircle2, value: totalCompleted.toLocaleString("en-IN"), label: "Completed", sub: `${completionRate}% completion rate` },
        { icon: Percent, value: `${avgConversion}%`, label: "Avg Conversion", sub: "Weighted by enrollment" },
    ];

    return (
        <div id="sec-campaign-automation" className={`h-full flex flex-col overflow-y-auto overflow-x-hidden py-6 ${THEMED_SCROLLBAR}`}>
            <button
                type="button"
                onClick={() => navigate("/app/dashboard-preview")}
                className="shrink-0 self-start mb-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium bg-white/5 border border-white/10 text-gray-300 hover:text-orange-400 hover:border-orange-600/40 hover:bg-orange-600/10 transition"
            >
                <ArrowLeft size={15} /> Back
            </button>
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-white">Campaign Automation</h1>
                    <p className="text-gray-400 mt-1.5 text-sm">
                        Build and manage automated marketing workflows.
                    </p>
                </div>

                <button
                    onClick={() => setModalOpen(true)}
                    className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-orange-600 transition"
                >
                    <Plus size={16} />
                    Create Automation
                </button>
            </div>

            {/* Tabs */}
            <div className="mt-8 flex items-center gap-5 border-b border-orange-600/10 shrink-0 overflow-x-auto">
                {TABS.map((tab) => {
                    const Icon = tab.icon;
                    const active = activeTab === tab.label;
                    return (
                        <button
                            key={tab.label}
                            type="button"
                            onClick={() => setActiveTab(tab.label)}
                            className={`relative flex items-center gap-1.5 pb-3 text-sm whitespace-nowrap transition ${active ? "text-orange-500 font-semibold" : "text-gray-400 hover:text-white"}`}
                        >
                            <Icon size={14} />
                            {tab.label}
                            {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-orange-500 rounded-full" />}
                        </button>
                    );
                })}
            </div>

            {/* Tab panels — all stay mounted (just hidden) so nothing resets while switching tabs:
                an in-progress AI generation, calendar modals and scheduled data keep working exactly as before. */}
            <div className={activeTab === "Create Campaign" ? "" : "hidden"}>
            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5 mt-6">
                {automationKpis.map((kpi, index) => {
                    const Icon = kpi.icon;
                    return (
                        <div
                            key={index}
                            className={`bg-black border rounded-2xl p-5 ${kpi.muted ? "border-white/10 opacity-60" : "border-orange-600/30"}`}
                        >
                            <Icon className={`mb-3 ${kpi.muted ? "text-gray-500" : "text-orange-500"}`} size={20} />
                            <h3 className="text-2xl font-bold text-white">{kpi.value}</h3>
                            <p className="text-gray-400 text-xs mt-1">{kpi.label}</p>
                            <p className={`text-xs mt-1 font-semibold ${kpi.muted ? "text-gray-500" : "text-green-400"}`}>{kpi.sub}</p>
                        </div>
                    );
                })}
            </div>

            {/* Automation table */}
            <div className="shrink-0 bg-black border border-orange-600/30 rounded-2xl mt-8 overflow-hidden flex flex-col">
                <div className={`overflow-x-auto ${THEMED_SCROLLBAR}`}>
                    <table className="w-full text-sm text-left border-collapse">
                        <thead>
                            <tr
                                role="button"
                                tabIndex={0}
                                aria-expanded={showBody}
                                title={showBody ? "Click to hide workflows" : "Click to show workflows"}
                                onClick={() => setTableOpen((o) => !o)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setTableOpen((o) => !o);
                                    }
                                }}
                                className={`cursor-pointer select-none text-gray-500 hover:text-gray-300 hover:bg-white/5 transition text-xs uppercase tracking-wide ${showBody ? "border-b border-orange-600/20" : ""}`}
                            >
                                <th className="px-6 py-4 font-semibold">Workflow Name</th>
                                <th className="px-6 py-4 font-semibold">Channel</th>
                                <th className="px-6 py-4 font-semibold">Status</th>
                                <th className="px-6 py-4 font-semibold">Enrolled</th>
                                <th className="px-6 py-4 font-semibold">Completed</th>
                                <th className="px-6 py-4 font-semibold">Conversion</th>
                                <th className="px-6 py-4 font-semibold">Budget</th>
                                <th className="px-6 py-4 font-semibold text-right">
                                    <span className="inline-flex items-center justify-end gap-2">
                                        Actions
                                        <ChevronDown
                                            size={16}
                                            className={`text-orange-500 transition-transform duration-200 ${showBody ? "rotate-180" : ""}`}
                                        />
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className={showBody ? "" : "hidden"}>
                            {loading && (
                                <tr>
                                    <td colSpan={8} className="px-6 py-10 text-center text-gray-400">
                                        Loading automations...
                                    </td>
                                </tr>
                            )}
                            {!loading && loadError && (
                                <tr>
                                    <td colSpan={8} className="px-6 py-10 text-center text-red-400">
                                        {loadError}
                                    </td>
                                </tr>
                            )}
                            {!loading && !loadError && automations.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="px-6 py-10 text-center text-gray-400">
                                        No automations yet — click "Create Automation" to add one.
                                    </td>
                                </tr>
                            )}
                            {!loading && !loadError && automations.map((a, index) => (
                                <tr
                                    key={a.id ?? index}
                                    className="border-b border-white/5 last:border-b-0 hover:bg-white/5 transition"
                                >
                                    <td className="px-6 py-4 text-white font-medium whitespace-nowrap">{a.workflow_name}</td>
                                    <td className="px-6 py-4 text-orange-400 whitespace-nowrap">{a.channel}</td>
                                    <td className="px-6 py-4">
                                        <span
                                            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${statusStyles[a.status]}`}
                                        >
                                            {a.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-gray-300">{a.enrolled}</td>
                                    <td className="px-6 py-4 text-gray-300">{a.completed}</td>
                                    <td className="px-6 py-4 text-orange-400 font-semibold">{Number(a.conversion_rate).toFixed(1)}%</td>
                                    <td className="px-6 py-4 text-gray-300">{formatBudget(a.budget)}</td>
                                    <td className="px-6 py-4 text-right">
                                        <button
                                            onClick={() => {
                                                setSaveEditError("");
                                                setEditingAutomation(a);
                                            }}
                                            title="Update stats"
                                            className="inline-flex items-center gap-1.5 border border-white/10 hover:border-orange-500/50 hover:text-orange-400 text-gray-300 px-2.5 py-1.5 rounded-lg text-xs font-medium transition"
                                        >
                                            <Pencil size={12} /> Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            </div>

            <div className={activeTab === "Generate Content" ? "mt-5" : "hidden"}>
            {/* STEP 1 — AI Media Generator (upload → generate). Generated content is saved to the calendar's library. */}
            <div
                id="sec-ai-media-workflow"
                className="shrink-0 w-full bg-black border border-orange-600/30 rounded-2xl mt-1 overflow-hidden"
            >
                <AIMediaWorkflow embedded onContentReady={handleContentReady} />
            </div>

            {/* Bridge — appears once content is generated */}
            {readyContent && (
                <div className="shrink-0 mt-5 flex flex-wrap items-center gap-4 rounded-2xl border border-green-500/30 bg-green-500/5 px-5 py-4">
                    <CheckCircle2 size={22} className="text-green-400 shrink-0" />
                    <div className="flex-1 min-w-[200px]">
                        <p className="text-sm font-semibold text-white">Content generated &amp; saved</p>
                        <p className="text-xs text-gray-400 mt-0.5">Next, choose which platforms to post on, and the date &amp; time.</p>
                    </div>
                    <button
                        type="button"
                        onClick={handleScheduleReady}
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition"
                    >
                        <CalendarDays size={16} /> Schedule this content
                    </button>
                </div>
            )}
            </div>

            <div className={activeTab === "Schedule & Post" ? "mt-5" : "hidden"}>
            {/* STEP 2 — Content Calendar (pick platforms, date & time → n8n auto-posts) */}
            <div
                id="sec-content-calendar"
                className="shrink-0 w-full bg-black border border-orange-600/30 rounded-2xl mt-1 overflow-hidden"
            >
                <CampaignCalendar store={calendarStore} scheduleRequest={scheduleRequest} />
            </div>
            </div>

            <div className={activeTab === "Planned Posts" ? "mt-5" : "hidden"}>
                <div className="shrink-0 w-full bg-black border border-orange-600/30 rounded-2xl overflow-hidden">
                    <PlannedPosts store={calendarStore} />
                </div>
            </div>

            {isModalOpen && (
                <CreateAutomationModal
                    onClose={() => setModalOpen(false)}
                    onCreate={handleCreate}
                    creating={creating}
                    createError={createError}
                />
            )}

            {editingAutomation && (
                <EditAutomationModal
                    automation={editingAutomation}
                    onClose={() => setEditingAutomation(null)}
                    onSave={handleUpdateStats}
                    saving={savingEdit}
                    saveError={saveEditError}
                />
            )}
        </div>
    );
};

export default CampaignAutomation;